// ─── AnimatedSection ──────────────────────────────────────────
// Universal animation wrapper. Every section uses this.
// Reads from animation presets, never hardcodes motion values.
// Supports reduced-motion for accessibility.

import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { getPreset } from './presets';

/**
 * Wraps a section or element with animation from the preset library.
 */
export function AnimatedSection({ preset = 'fade-up', delay = 0, className = '', style, children, disableAnimation = false, ...rest }) {
  const ref = useRef(null);
  const prefersReduced = useReducedMotion();
  const p = getPreset(preset, false, prefersReduced);
  const inView = useInView(ref, { once: p.viewport?.once ?? true, margin: '-40px' });

  // Motion 1.0 — when the global motionPreset already drives the surrounding
  // animation (e.g. Soft Reveal at the canvas level), bypass to avoid stacking
  // two fades on the same content.
  if (disableAnimation || preset === 'none' || !p.id) {
    return <div className={className} style={style} {...rest}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={p.initial}
      animate={inView ? p.animate : p.initial}
      transition={{ ...p.transition, delay: (p.transition?.delay || 0) + delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * Wraps staggered children (e.g., hero text lines, card grids).
 * Each direct child gets animated with a stagger delay.
 */
export function StaggerContainer({ preset = 'stagger-text', className = '', style, children }) {
  const ref = useRef(null);
  const prefersReduced = useReducedMotion();
  const p = getPreset(preset, false, prefersReduced);
  const inView = useInView(ref, { once: p.viewport?.once ?? true, margin: '-40px' });

  if (!p.isStagger) {
    return <div className={className} style={style}>{children}</div>;
  }

  return (
    <div ref={ref} className={className} style={style}>
      {React.Children.map(children, (child, i) => {
        if (!React.isValidElement(child)) return child;
        return (
          <motion.div
            initial={p.child.initial}
            animate={inView ? p.child.animate : p.child.initial}
            transition={{
              ...p.child.transition,
              delay: (p.containerDelay || 0) + i * (p.childStagger || 0.1),
            }}
          >
            {child}
          </motion.div>
        );
      })}
    </div>
  );
}
